import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { checkEmailSpam } from '../util/api';

const authenticated = authInfo => authInfo && authInfo.isAnonymous === false;

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/Auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/Auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/Auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/Auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/Auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/Auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/Auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/Auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/Auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/Auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/Auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/Auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/Auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/Auth/CONFIRM_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (kept for ConfirmPage route compatibility)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [] };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.Auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  return sdk
    .login({ username, password })
    .then(() => {
      if (typeof window === 'object') {
        window.dataLayer.push({
          event: 'login',
          params: {
            method: 'email',
          },
        });
      }

      return dispatch(loginSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  return sdk
    .logout()
    .then(() => {
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());

  const {
    email,
    password,
    firstName,
    lastName,
    account_role,
    isCompanyAbroad,
    phoneNumber,
    howDidYouHear,
    howDidYouHearOther,
    // Customer-specific fields
    lookingFor,
    lookingForText,
    roleAtCompany,
    workedWithFreelancers,
    languages,
    location,
  } = params;

  // Retrieve first landing URL and parse gclid
  const firstLandingURL =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('firstSessionUrl') : null;
  let gclid = null;
  if (firstLandingURL) {
    try {
      const urlParams = new URLSearchParams(firstLandingURL.split('?')[1]);
      gclid = urlParams.get('gclid');
    } catch (e) {
      // Silent fail if URL parsing fails
    }
  }

  const notificationLanguage =
    Array.isArray(languages) && languages.includes('finnish') ? 'finnish' : 'english';

  const selectedPlace = location?.selectedPlace || {};
  const locationCity = selectedPlace.address ? selectedPlace.address.split(',')[0]?.trim() : '';
  const locationCountry = selectedPlace.country || '';
  const locationCountryCode = selectedPlace.countryCode || '';

  const createUserParams = {
    email,
    password,
    firstName,
    lastName,
    publicData: {
      account_role,
      ...(languages && languages.length > 0 && { languages }),
      ...(location && {
        location,
        locationCity,
        locationCountry,
        locationCountryCode,
      }),
    },
    protectedData: {
      phoneNumber,
      textMessageNotifications: true,
      ...(lookingFor && { lookingFor }),
      ...(lookingForText && { lookingForText }),
      ...(roleAtCompany && { roleAtCompany }),
      ...(workedWithFreelancers && { workedWithFreelancers }),
    },
    privateData: {
      howDidYouHear,
      ...(howDidYouHearOther && { howDidYouHearOther }),
      isCompanyAbroad,
      firstLandingURL,
      gclid,
      notificationLanguage,
    },
  };

  return sdk.currentUser
    .create(createUserParams)
    .then(() => {
      if (typeof window === 'object') {
        window.dataLayer.push({
          event: 'sign_up',
          params: {
            method: 'email',
          },
        });
      }

      return dispatch(signupSuccess());
    })
    .then(() => dispatch(login(email, password)))
    .then(() => {
      // Fire-and-forget: check email for spam/disposable
      const currentUserId = getState().user.currentUser?.id?.uuid;
      if (currentUserId) {
        checkEmailSpam({ userId: currentUserId, email }).catch(e => {
          console.error('Email spam check failed:', e);
        });
      }

      return sdk.ownListings.create({
        title: 'Profile listing',
        description: 'Profile listing',
        publicData: {
          listingType: 'profile',
          authorRole: [account_role],
        },
      });
    })
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
      });
    });
};

// Kept for ConfirmPage route compatibility
export const signupWithIdp = () => () => {
  return Promise.reject(new Error('IdP signup is no longer supported'));
};

export const createSignUpProjectListing = () => () => {};
